import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface HummingbirdLoaderProps {
  title?: string;
  subtitle?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * Polished loading state with a hummingbird wing-flutter animation,
 * status copy, and a structured skeleton beneath. Use for heavy
 * transitions (cart → builder, checkout init, etc.).
 */
export function HummingbirdLoader({
  title = "Just a moment…",
  subtitle = "Preparing your workspace",
  className,
  size = "md",
}: HummingbirdLoaderProps) {
  const sizeMap = {
    sm: { wrap: "h-20 w-24", body: "h-10 w-14", wing: "h-6 w-10" },
    md: { wrap: "h-32 w-40", body: "h-14 w-20", wing: "h-9 w-14" },
    lg: { wrap: "h-44 w-56", body: "h-20 w-28", wing: "h-12 w-20" },
  }[size];

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-6 rounded-3xl bg-gradient-soft p-12",
        className,
      )}
    >
      <div className={cn("relative", sizeMap.wrap)}>
        {/* Hummingbird body */}
        <motion.div
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-hero shadow-pop",
            sizeMap.body,
          )}
          animate={{
            y: [0, -3, 0, 2, 0],
            rotate: [-2, 2, -2],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {/* Beak */}
          <div className="absolute -right-3 top-1/2 h-1.5 w-4 -translate-y-1/2 rounded-full bg-amber-400" />
          {/* Eye */}
          <div className="absolute right-3 top-1/3 h-1.5 w-1.5 rounded-full bg-white" />
        </motion.div>

        {/* Upper wing */}
        <motion.div
          className={cn(
            "absolute left-1/2 top-1/3 -translate-x-1/2 rounded-full bg-primary/40",
            sizeMap.wing,
          )}
          style={{ originX: 0.5, originY: 1 }}
          animate={{
            scaleY: [1, 0.2, 1, 0.2, 1],
            opacity: [0.7, 0.3, 0.7, 0.3, 0.7],
          }}
          transition={{
            duration: 0.25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        {/* Lower wing */}
        <motion.div
          className={cn(
            "absolute left-1/2 bottom-1/3 -translate-x-1/2 rounded-full bg-primary/30",
            sizeMap.wing,
          )}
          style={{ originX: 0.5, originY: 0 }}
          animate={{
            scaleY: [1, 0.2, 1, 0.2, 1],
            opacity: [0.6, 0.2, 0.6, 0.2, 0.6],
          }}
          transition={{
            duration: 0.25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.12,
          }}
        />
      </div>

      <div className="text-center space-y-1">
        <p className="font-semibold text-base">{title}</p>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

/** Skeleton block for use alongside the loader. */
export function LoadingSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
      <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
      <div className="h-32 w-full rounded-2xl bg-muted animate-pulse" />
      <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
    </div>
  );
}
